/**
 * NARRATIVETOAST.TSX - AI-Powered Game Event Narration
 * Displays dramatic narrative commentary for key game events
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNarrative } from '../hooks';
import type { EventType } from '../services/narrative';

interface NarrativeToastProps {
  event?: {
    type: EventType;
    team?: number;
    player?: string;
    amount?: string;
    details?: string;
  };
  roundId: number;
  onDismiss?: () => void;
  autoHide?: number; // milliseconds
}

// Faction colors derived from canonical FACTIONS config
const FACTION_STYLES = {
  0: { primary: 'from-cyan-500 to-cyan-700', glow: 'shadow-cyan-500/50', name: 'Raven' },
  1: { primary: 'from-red-500 to-red-700', glow: 'shadow-red-500/50', name: 'Ape' },
  2: { primary: 'from-yellow-500 to-amber-600', glow: 'shadow-yellow-500/50', name: 'Moth' },
  3: { primary: 'from-green-500 to-emerald-600', glow: 'shadow-green-500/50', name: 'Viper' },
  4: { primary: 'from-purple-500 to-violet-600', glow: 'shadow-purple-500/50', name: 'Rabbit' },
  5: { primary: 'from-teal-500 to-cyan-600', glow: 'shadow-teal-500/50', name: 'Wolf' },
  6: { primary: 'from-pink-500 to-magenta-600', glow: 'shadow-pink-500/50', name: 'Fox' },
  7: { primary: 'from-gray-400 to-white', glow: 'shadow-white/50', name: 'Owl' },
};

export const NarrativeToast: React.FC<NarrativeToastProps> = ({
  event,
  roundId,
  onDismiss,
  autoHide = 8000,
}) => {
  const { narrate, narrator } = useNarrative({ roundId });
  const [isVisible, setIsVisible] = useState(false);
  const [narrative, setNarrative] = useState<string>('');

  useEffect(() => {
    if (!event) return;

    // Generate narrative for the event
    const generateNarrative = async () => {
      try {
        const result = await narrate(event.type, {
          team: event.team,
          player: event.player,
          amount: event.amount,
          ...event.details ? { details: event.details } : {},
        });

        if (result?.narrative) {
          setNarrative(result.narrative);
          setIsVisible(true);
        }
      } catch (error) {
        console.error('Failed to generate narrative:', error);
        // Fallback to a simple message
        const factionName = event.team !== undefined 
          ? FACTION_STYLES[event.team as keyof typeof FACTION_STYLES]?.name || 'Unknown'
          : 'a player';
        setNarrative(`${factionName} makes their move against THALIA...`);
        setIsVisible(true);
      }
    };

    generateNarrative();
  }, [event, narrate]);

  useEffect(() => {
    if (isVisible && autoHide > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onDismiss?.(), 300);
      }, autoHide);
      return () => clearTimeout(timer);
    }
  }, [isVisible, autoHide, onDismiss]);

  const factionStyles = event?.team !== undefined 
    ? FACTION_STYLES[event.team as keyof typeof FACTION_STYLES] 
    : { primary: 'from-neon-cyan to-neon-magenta', glow: 'shadow-neon-cyan/30', name: 'THALIA' };

  return (
    <AnimatePresence>
      {isVisible && narrative && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="fixed bottom-24 left-1/2 transform -translate-x-1/2 z-50 max-w-xl w-full px-4"
        >
          <div 
            className={`
              relative glass-panel-3d p-4 border border-white/10
              shadow-lg ${factionStyles.glow}
            `}
          >
            {/* Gradient accent bar */}
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${factionStyles.primary} rounded-t-lg`} />
            
            {/* Narrator badge */}
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${factionStyles.primary} animate-pulse`} />
              <span className="text-xs text-white/50 uppercase tracking-wider">
                {narrator?.name || 'THALIA'}
              </span>
            </div>

            {/* Narrative text */}
            <p className="text-white/90 text-sm md:text-base leading-relaxed italic">
              "{narrative}"
            </p>

            {/* Dismiss button */}
            <button
              onClick={() => {
                setIsVisible(false);
                setTimeout(() => onDismiss?.(), 300);
              }}
              className="absolute top-2 right-2 text-white/30 hover:text-white/70 transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Simpler inline narrative for activity feed
export const NarrativeText: React.FC<{
  text: string;
  narrator?: string;
  team?: number;
}> = ({ text, narrator, team }) => {
  const factionStyles = team !== undefined 
    ? FACTION_STYLES[team as keyof typeof FACTION_STYLES] 
    : null;

  return (
    <div className="relative">
      {narrator && (
        <span className="text-xs text-white/40 block mb-1">
          — {narrator}
        </span>
      )}
      <p className={`
        text-sm italic text-white/70
        ${factionStyles ? `border-l-2 pl-2 border-gradient-to-b ${factionStyles.primary}` : ''}
      `}>
        "{text}"
      </p>
    </div>
  );
};

export default NarrativeToast;
